'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";

import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {isFalse} from "@uw-it-sis/lib-react/lib/Utils";
import UnknownState from "@uw-it-sis/lib-react/lib/UnknownState";

import CourseSummaryWidgetStore from './CourseSummaryWidgetStore';
import {normalize} from './CourseUtils';
import CourseSummary from './CourseSummary';
import CourseMessages from './CourseMessages';
import AddCourseToPlanForm from './AddCourseToPlanForm';
import BookmarkWidget from './BookmarkWidget';
import planItemStore from "./PlanItemStore";

const analyticsStore = appStore.getAnalyticsStore();

/**
 * Course summary popover widget.
 */
class CourseSummaryWidget extends Component {

    constructor(props) {
        super(props);
        this.props = props;
        this.store = new CourseSummaryWidgetStore();

        this.state = {
            addPlanFormActive: false
        };

        this.handleAddToPlanClick = this.handleAddToPlanClick.bind(this);
        this.resetPlanFormActive = this.resetPlanFormActive.bind(this);
        this.handleEntered = this.handleEntered.bind(this);
        this.showModal = this.showModal.bind(this);
        this.hideModal = this.hideModal.bind(this);
    }

    componentDidMount() {
        /*
         * NOTE: We can't load the course summary and messages data here because there's it would cause a huge amount of
         * requests for data that may not even be used. Data has to be loaded when the modal is shown.
         */
        this.store.mount(this.props.course.id, this.props.course.courseCode);
        if(this.props.openInPlanModal) {
            this.showModal();
        }
    }

    componentDidUpdate(prevProps) {
        if(this.props.dt !== prevProps.dt && this.props.openInPlanModal) {
            this.store.mount(this.props.course.id, this.props.course.courseCode);
            this.showModal();
        }
    }

    componentWillUnmount() {
        // Get rid of the observers.
        this.store.unmount();
    }

    /**
     * Click handler for the "add to plan" button for adding a recommended course.
     */
    handleAddRecommendedToPlanClick = () => {
        planItemStore.acceptRecommendation(this.props.planItemId);
        this.hideModal();
    };

    /**
     * Click handler for the "add to plan" button.
     */
    handleAddToPlanClick = () => {
        this.store.updateToggle(true);
        this.setState({addPlanFormActive : true});
    };

    resetPlanFormActive = () => {
        this.store.updateToggle(false);
        this.setState({addPlanFormActive : false});
    };

    handleEntered = () => {
        analyticsStore.addEvent(`${this.props.analyticsContext}-Course-Popover`, {},
            {cCode: this.props.course.courseCode});
        this.store.initialize();
    };

    showModal = () => {
        this.store.updateToggle(true);
        this.handleEntered();
    };

    /**
     * Callback for when the overlay is closed (by clicking away or whatever).
     * This should reset the component.
     */
    hideModal = () => {
        this.store.resetState();
        this.store.updateToggle(false);
    };

    _getModalBody(courseCode, buttons, courseDetailsPageLink) {

        let messagesWidget = null;

        if (appStore.isAuthenticated()) {
            messagesWidget = <CourseMessages messages={this.store.getCourseMessagesData()}/>;
        }

        return (
            <Modal.Body>
                <CourseSummary courseSummary={this.store.getCourseSummaryData(courseCode)}/>
                {courseDetailsPageLink}
                {messagesWidget}
                {buttons}
            </Modal.Body>
        );
    }

    _makeOverlay() {
        let overlay;
        let id = this.props.course.id;
        let subject = this.props.course.subject;
        let number = this.props.course.number;
        let title = this.props.course.title;
        let text = this.props.course.text;
        let credit = "(" + this.props.course.credit + ")";
        let courseCode = subject + " " + number;

        // Only show if the user is authenticated.
        let buttons =
            <ButtonToolbar className="justify-content-end mt-3">
                <Button variant="secondary" onClick={this.hideModal}>Close</Button>
            </ButtonToolbar>;
        let cdpUrl = `/course/#/courses/${normalize(courseCode)}`;

        if (appStore.isAuthenticated() && ! appStore.isAdviser()) {
            buttons =
                <ButtonToolbar className="justify-content-end mt-3">
                    <Button variant="primary" onClick={this.handleAddToPlanClick}>Add to Plan</Button>
                    <BookmarkWidget showSuccess={true}
                                    code={courseCode} type={'kuali.lu.type.CreditCourse'} component={'button'}
                                    analyticsContext={this.props.analyticsContext + "-Course-Popover"} toggle={true}/>
                </ButtonToolbar>;
        }

        let titleText = <>{courseCode} &ndash; {title}</>;
        if (this.props.openInPlanModal) {
            titleText = <>{courseCode} &ndash; {title} &nbsp; {credit}</>;
        }
        const titleComponent = <a href={cdpUrl}>{titleText}</a>;
        const courseDetailsPageLink = <div className="mb-3"><a title={"View more details about " + courseCode} href={cdpUrl} onClick={this.hideModal} >View more details about {courseCode}</a></div>;

        if (appStore.isAuthenticated()) {
            if (this.props.openInPlanModal) {
                if (this.props.course.type === "recommended") {
                    buttons =
                        <ButtonToolbar className="justify-content-end mt-3">
                            <Button variant="primary" onClick={this.handleAddRecommendedToPlanClick}>Add to Plan</Button>
                            <Button variant="secondary" onClick={this.hideModal}>Close</Button>
                        </ButtonToolbar>
                } else {
                    buttons =
                        <ButtonToolbar className="justify-content-end mt-3">
                            <Button variant="secondary" onClick={this.hideModal}>Close</Button>
                        </ButtonToolbar>
                }
            }
        }

        if (this.state.addPlanFormActive) {
            overlay =
                <Modal id={"modal-add-to-plan-" + id}
                       show={this.store.toggleModal}
                       onHide={this.hideModal}
                       onExited={this.resetPlanFormActive}
                       className="course-modal"
                       aria-labelledby={"modal-add-to-plan-" + id + "-title"}
                       centered>
                    <Modal.Header closeButton>
                        <Modal.Title id={"modal-add-to-plan-" + id + "-title"}
                                     onClick={this.hideModal}>
                            {titleComponent}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <AddCourseToPlanForm course={this.store.getCourseSummaryData(courseCode)} onCancel={this.hideModal}
                                             analyticsContext={this.props.analyticsContext + "-Course-Popover"}/>
                    </Modal.Body>
                </Modal>;
        } else {
            let modalBody = this._getModalBody(courseCode, buttons, courseDetailsPageLink );
            overlay =
                <Modal id={"modal-course-summary-" + id}
                       show={this.store.toggleModal}
                       onHide={this.hideModal}
                       onExited={this.resetPlanFormActive}
                       className="course-modal"
                       aria-labelledby={"modal-course-summary-" + id + "-title"}
                       centered>
                    <Modal.Header closeButton>
                        <Modal.Title id={"modal-course-summary-" + id + "-title"}
                                     onClick={this.hideModal}>
                            {titleComponent}
                        </Modal.Title>
                    </Modal.Header>
                    {modalBody}
                </Modal>
        }

        let widgetTrigger;
        if ( isFalse(this.props.openInPlanModal) ) {
            widgetTrigger = <Button variant="link" onClick={this.showModal}>{text}</Button>;
        }

        return (
            <>
                {widgetTrigger}
                {overlay}
            </>);
    }

    render() {
        let component;

        let widgetTrigger = null;
        if ( isFalse(this.props.openInPlanModal) ) {
            widgetTrigger = <Button variant="link" onClick={this.showModal}>{this.props.course.text}</Button>;
        }

        switch (this.store.state) {
            case States.initial:
            case States.pending:
                component =
                    <>
                        {/* In states pending and error the button still needs to be displayed. */}
                        {widgetTrigger}
                        <Modal id="modal-loading-course-summary"
                               show={this.store.toggleModal}
                               onHide={this.hideModal}
                               onExited={this.resetPlanFormActive}
                               aria-labelledby="modal-loading-course-summary-title"
                               centered>
                            <Modal.Header className="sr-only">
                                <Modal.Title id="modal-loading-course-summary-title">
                                    Loading Course Summary
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="text-center my-4">
                                    <Spinner animation="border" role="status">
                                        <span className="sr-only">Loading Course Summary...</span>
                                    </Spinner>
                                </div>
                            </Modal.Body>
                        </Modal>
                    </>;
                break;
            case States.done:
                component = this._makeOverlay();
                break;
            case States.error:
                component =
                     <>
                        {widgetTrigger}
                        <Modal id="modal-course-summary-error"
                               show={this.store.toggleModal}
                               onHide={this.hideModal}
                               onExited={this.resetPlanFormActive}
                               aria-labelledby="modal-course-summary-error-title"
                               centered>
                            <Modal.Header className="sr-only">
                                <Modal.Title id="modal-course-summary-error-title">
                                    Error
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Alert variant="danger">Failed to get the Course Information. Please try again.</Alert>
                            </Modal.Body>
                        </Modal>
                     </>;
                break;
            default:
                component = <UnknownState/>;
        }


        return component;
    }
}

CourseSummaryWidget.propTypes = {
    course: PropTypes.object.isRequired,
    analyticsContext: PropTypes.string.isRequired,
    planItemId: PropTypes.string, // needed only for accept recommendation
    openInPlanModal: PropTypes.bool
};

CourseSummaryWidget.defaultProps = {
    course: {},
    analyticsContext: '',
    planItemId: '',
    openInPlanModal: false
}

export default observer(CourseSummaryWidget);
